import React, { useState, useMemo } from 'react';
import { Helmet } from 'react-helmet-async';
import { teamsData } from '../data/teamsData';
import { raceData } from '../data/raceData';
import { resultsData } from '../data/resultsData';
import '../App.css';

// Constantes del sistema de puntuación FIA oficial
const POINTS_SYSTEM = {
    RACE: [25, 18, 15, 12, 10, 8, 6, 4, 2, 1], // Top 10
    SPRINT: [8, 7, 6, 5, 4, 3, 2, 1]           // Top 8
};

// Generar lista de todos los pilotos a partir de teamsData
const allDrivers = teamsData.flatMap(team => [
    { name: team.driver1.name, flag: team.driver1.flag, team: team.team, color: team.color },
    { name: team.driver2.name, flag: team.driver2.flag, team: team.team, color: team.color }
]).sort((a, b) => a.name.localeCompare(b.name));

const Simulator = ({ t, language }) => {
    const [standingsTab, setStandingsTab] = useState('drivers'); // 'drivers' or 'constructors'
    const [expandedGpId, setExpandedGpId] = useState(null);

    // Inicializar el estado de simulación para carreras no completadas
    const [simulatedGps, setSimulatedGps] = useState(() => {
        const initialState = {};
        raceData.forEach(race => {
            if (race.isSuspended) return;

            const actual = resultsData[race.id];
            const isCompleted = actual && actual.raceResults !== null;

            if (isCompleted) {
                // Almacenar los resultados reales de carreras completadas (bloqueadas)
                initialState[race.id] = {
                    raceResults: actual.raceResults.slice(0, 10).map(r => r.driver),
                    sprintResults: actual.sprintResults ? actual.sprintResults.slice(0, 8).map(r => r.driver) : null
                };
            } else {
                // Carreras pendientes: vacías por defecto
                initialState[race.id] = {
                    raceResults: Array(10).fill(''),
                    sprintResults: race.isSprint ? Array(8).fill('') : null
                };
            }
        });
        return initialState;
    });

    // Identificar qué carreras están bloqueadas (reales) y cuáles son pendientes (simuladas)
    const gpStatusList = useMemo(() => {
        return raceData.filter(r => !r.isSuspended).map(race => {
            const actual = resultsData[race.id];
            const isCompleted = actual && actual.raceResults !== null;
            return {
                id: race.id,
                gp: race.gp,
                city: race.city,
                flag: race.flag,
                isSprint: race.isSprint,
                isCompleted
            };
        });
    }, []);

    // Encontrar el primer GP simulable para expandirlo inicialmente
    useMemo(() => {
        if (expandedGpId === null) {
            const firstPending = gpStatusList.find(gp => !gp.isCompleted);
            if (firstPending) {
                setExpandedGpId(firstPending.id);
            } else {
                setExpandedGpId(1);
            }
        }
    }, [gpStatusList, expandedGpId]);

    // Clasificación base inicial fija (según tabla de clasificación oficial correcta)
    const baseStandings = useMemo(() => {
        const driverPoints = {
            "Kimi Antonelli": 219,
            "Lewis Hamilton": 169,
            "George Russell": 160,
            "Charles Leclerc": 138,
            "Lando Norris": 128,
            "Max Verstappen": 109,
            "Oscar Piastri": 92,
            "Isack Hadjar": 68,
            "Liam Lawson": 43,
            "Pierre Gasly": 42,
            "Arvid Lindblad": 23,
            "Franco Colapinto": 19,
            "Oliver Bearman": 18,
            "Gabriel Bortoleto": 10,
            "Carlos Sainz": 6,
            "Alex Albon": 5,
            "Esteban Ocon": 3,
            "Nico Hülkenberg": 2,
            "Fernando Alonso": 1,
            "Valtteri Bottas": 0,
            "Sergio Pérez": 0,
            "Lance Stroll": 0
        };

        const constructorPoints = {
            "Mercedes": 379,
            "Ferrari": 307,
            "McLaren": 220,
            "Red Bull Ford": 177,
            "Racing Bulls (RB)": 66,
            "Alpine": 61,
            "Haas": 21,
            "Audi": 12,
            "Williams": 11,
            "Aston Martin Honda": 1,
            "Cadillac": 0
        };

        return { driverPoints, constructorPoints };
    }, []);

    // Recalcular posiciones del campeonato en tiempo real mezclando base + simulaciones
    const currentStandings = useMemo(() => {
        const driverPoints = { ...baseStandings.driverPoints };
        const constructorPoints = { ...baseStandings.constructorPoints };

        // Sumar puntos de las carreras simuladas
        Object.keys(simulatedGps).forEach(gpId => {
            const gpIdNum = parseInt(gpId);
            const status = gpStatusList.find(gp => gp.id === gpIdNum);
            
            // Solo procesar si el GP es simulado (no bloqueado)
            if (status && !status.isCompleted) {
                const simulated = simulatedGps[gpId];

                // 1. Puntos Carrera Principal (Top 10)
                simulated.raceResults.forEach((driverName, idx) => {
                    if (driverName && driverPoints[driverName] !== undefined) {
                        const pts = POINTS_SYSTEM.RACE[idx] || 0;
                        driverPoints[driverName] += pts;
                        
                        // Encontrar la escudería del piloto
                        const driverInfo = allDrivers.find(d => d.name === driverName);
                        if (driverInfo) {
                            constructorPoints[driverInfo.team] += pts;
                        }
                    }
                });

                // 2. Puntos Sprint (Top 8)
                if (simulated.sprintResults) {
                    simulated.sprintResults.forEach((driverName, idx) => {
                        if (driverName && driverPoints[driverName] !== undefined) {
                            const pts = POINTS_SYSTEM.SPRINT[idx] || 0;
                            driverPoints[driverName] += pts;

                            const driverInfo = allDrivers.find(d => d.name === driverName);
                            if (driverInfo) {
                                constructorPoints[driverInfo.team] += pts;
                            }
                        }
                    });
                }
            }
        });

        // Formatear y ordenar Pilotos
        const sortedDrivers = allDrivers.map(d => ({
            ...d,
            points: driverPoints[d.name] || 0
        })).sort((a, b) => b.points - a.points);

        // Formatear y ordenar Constructores
        const sortedConstructors = teamsData.map(t => ({
            team: t.team,
            color: t.color,
            points: constructorPoints[t.team] || 0
        })).sort((a, b) => b.points - a.points);

        return { drivers: sortedDrivers, constructors: sortedConstructors };
    }, [baseStandings, simulatedGps, gpStatusList]);

    // Calcular puntos máximos restantes y determinar si hay campeón matemático anticipado
    const champCheck = useMemo(() => {
        const driversList = currentStandings.drivers;
        if (driversList.length === 0) return null;

        const leader = driversList[0];
        const runnerUp = driversList[1];
        if (!runnerUp) return null;

        // Calcular cuántos puntos máximos quedan disponibles en los GP simulables que aún no están decididos o faltan en el calendario
        let maxPointsRemaining = 0;
        gpStatusList.forEach(gp => {
            if (!gp.isCompleted) {
                // Si el GP aún es simulable
                maxPointsRemaining += 25; // 25 por victoria de carrera (sin vuelta rápida)
                if (gp.isSprint) {
                    maxPointsRemaining += 8; // 8 por victoria de sprint
                }
            }
        });

        const gap = leader.points - runnerUp.points;
        if (gap > maxPointsRemaining) {
            return {
                driver: leader.name,
                team: leader.team,
                color: leader.color,
                flag: leader.flag,
                gap,
                maxPointsRemaining
            };
        }
        return null;
    }, [currentStandings, gpStatusList]);

    // Manejar el cambio de piloto en una posición (con intercambio de posiciones para evitar duplicados)
    const handleSelectDriver = (gpId, sessionType, positionIndex, selectedDriverName) => {
        setSimulatedGps(prev => {
            const gpData = { ...prev[gpId] };
            const results = [...(sessionType === 'sprint' ? gpData.sprintResults : gpData.raceResults)];

            // Si el piloto ya estaba asignado a otra posición en la misma sesión, hacemos swap
            const prevIndex = results.indexOf(selectedDriverName);
            const targetPrevDriver = results[positionIndex]; // El piloto que estaba en la posición que ahora ocupamos

            if (selectedDriverName === '') {
                results[positionIndex] = '';
            } else {
                results[positionIndex] = selectedDriverName;
                if (prevIndex !== -1 && prevIndex !== positionIndex) {
                    // Mover el piloto desplazado a la antigua posición del nuevo piloto
                    results[prevIndex] = targetPrevDriver || '';
                }
            }

            if (sessionType === 'sprint') {
                gpData.sprintResults = results;
            } else {
                gpData.raceResults = results;
            }

            return {
                ...prev,
                [gpId]: gpData
            };
        });
    };



    // Limpiar predicciones de un GP específico
    const handleClearGp = (gpId) => {
        const gp = gpStatusList.find(g => g.id === gpId);
        if (!gp || gp.isCompleted) return;

        setSimulatedGps(prev => ({
            ...prev,
            [gpId]: {
                raceResults: Array(10).fill(''),
                sprintResults: gp.isSprint ? Array(8).fill('') : null
            }
        }));
    };

    // Reiniciar todo el simulador
    const handleResetAll = () => {
        setSimulatedGps(prev => {
            const nextState = {};
            Object.keys(prev).forEach(gpId => {
                const gpIdNum = parseInt(gpId);
                const status = gpStatusList.find(g => g.id === gpIdNum);
                
                if (status) {
                    if (status.isCompleted) {
                        nextState[gpId] = prev[gpId]; // Mantener reales
                    } else {
                        nextState[gpId] = {
                            raceResults: Array(10).fill(''),
                            sprintResults: status.isSprint ? Array(8).fill('') : null
                        };
                    }
                }
            });
            return nextState;
        });
    };

    // Insignias para medallas del Top 3
    const getPositionBadge = (pos) => {
        if (pos === 1) return <span className="position-medal gold">1</span>;
        if (pos === 2) return <span className="position-medal silver">2</span>;
        if (pos === 3) return <span className="position-medal bronze">3</span>;
        return <span className="position-num">{pos}</span>;
    };

    return (
        <div className="simulator-page">
            <Helmet>
                <title>{t.simulatorTitle} 2026 | F1 Calendar</title>
                <meta name="description" content={t.simulatorSubtitle} />
            </Helmet>

            <div className="simulator-header-section">
                <h2>{t.simulatorTitle}</h2>
                <p className="simulator-desc">{t.simulatorSubtitle}</p>
                <div className="simulator-global-controls">
                    <button className="btn-reset-simulator" onClick={handleResetAll}>
                        {t.resetAll}
                    </button>
                </div>
            </div>

            <div className="simulator-layout">
                {/* LÍNEA DE TIEMPO DE GP (COLUMNA IZQUIERDA) */}
                <div className="simulator-timeline-panel">
                    {gpStatusList.map(gp => {
                        const isExpanded = expandedGpId === gp.id;
                        const simulated = simulatedGps[gp.id];
                        const isGpCompleted = gp.isCompleted;

                        // Determinar el piloto ganador simulado o real para mostrar en el título resumido
                        let winnerName = null;
                        if (isGpCompleted) {
                            const actual = resultsData[gp.id];
                            winnerName = actual?.raceResults?.[0]?.driver;
                        } else if (simulated && simulated.raceResults[0]) {
                            winnerName = simulated.raceResults[0];
                        }

                        return (
                            <div 
                                key={gp.id} 
                                className={`gp-accordion-card ${isExpanded ? 'active' : ''} ${isGpCompleted ? 'locked' : ''}`}
                            >
                                <div 
                                    className="gp-accordion-header"
                                    onClick={() => setExpandedGpId(isExpanded ? null : gp.id)}
                                >
                                    <div className="gp-header-left">
                                        <span className="gp-flag-big">{gp.flag}</span>
                                        <div className="gp-header-text">
                                            <span className="gp-round-label">ROUND {gp.id}</span>
                                            <h3>{gp.gp}</h3>
                                            <span className="gp-city">{gp.city}</span>
                                        </div>
                                    </div>
                                    <div className="gp-header-right">
                                        {gp.isSprint && <span className="badge-sprint-tag">SPRINT WEEKEND</span>}
                                        {isGpCompleted ? (
                                            <span className="gp-status-badge locked">{t.lockedGP}</span>
                                        ) : (
                                            <span className="gp-status-badge pending">{t.pendingGP}</span>
                                        )}
                                        {winnerName && (
                                            <div className="gp-header-winner-preview">
                                                {winnerName}
                                            </div>
                                        )}
                                        <span className="accordion-arrow">{isExpanded ? '▲' : '▼'}</span>
                                    </div>
                                </div>

                                {isExpanded && (
                                    <div className="gp-accordion-body">
                                        {!isGpCompleted && (
                                            <div className="gp-body-controls">
                                                <button className="btn-clear-gp" onClick={() => handleClearGp(gp.id)}>
                                                    {t.clearGP}
                                                </button>
                                            </div>
                                        )}

                                        <div className="gp-selectors-grid">
                                            {/* COLUMNA CARRERA PRINCIPAL (TOP 10) */}
                                            <div className="gp-selectors-column">
                                                <h4>{t.raceResultsTitle}</h4>
                                                <div className="selectors-list">
                                                    {Array(10).fill(null).map((_, idx) => {
                                                        const pos = idx + 1;
                                                        const selectedDriver = simulated?.raceResults[idx] || '';
                                                        
                                                        return (
                                                            <div key={`race-p-${pos}`} className="position-selector-row">
                                                                <span className="pos-badge-num">{pos}</span>
                                                                {isGpCompleted ? (
                                                                    <div className="locked-driver-card">
                                                                        <span>{selectedDriver || '-'}</span>
                                                                    </div>
                                                                ) : (
                                                                    <select
                                                                        value={selectedDriver}
                                                                        onChange={(e) => handleSelectDriver(gp.id, 'race', idx, e.target.value)}
                                                                        className="custom-driver-select"
                                                                    >
                                                                        <option value="">-- {t.selectDriver} --</option>
                                                                        {allDrivers.map(d => (
                                                                            <option key={d.name} value={d.name}>
                                                                                {d.flag} {d.name} ({d.team})
                                                                            </option>
                                                                        ))}
                                                                    </select>
                                                                )}
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            </div>

                                            {/* COLUMNA SPRINT (TOP 8) */}
                                            {gp.isSprint && simulated?.sprintResults && (
                                                <div className="gp-selectors-column">
                                                    <h4>{t.sprintResultsTitle}</h4>
                                                    <div className="selectors-list">
                                                        {Array(8).fill(null).map((_, idx) => {
                                                            const pos = idx + 1;
                                                            const selectedDriver = simulated.sprintResults[idx] || '';

                                                            return (
                                                                <div key={`sprint-p-${pos}`} className="position-selector-row">
                                                                    <span className="pos-badge-num sprint">{pos}</span>
                                                                    {isGpCompleted ? (
                                                                        <div className="locked-driver-card">
                                                                            <span>{selectedDriver || '-'}</span>
                                                                        </div>
                                                                    ) : (
                                                                        <select
                                                                            value={selectedDriver}
                                                                            onChange={(e) => handleSelectDriver(gp.id, 'sprint', idx, e.target.value)}
                                                                            className="custom-driver-select"
                                                                        >
                                                                            <option value="">-- {t.selectDriver} --</option>
                                                                            {allDrivers.map(d => (
                                                                                <option key={d.name} value={d.name}>
                                                                                    {d.flag} {d.name} ({d.team})
                                                                                </option>
                                                                            ))}
                                                                        </select>
                                                                    )}
                                                                </div>
                                                            );
                                                        })}
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>

                {/* CLASIFICACIÓN DINÁMICA (COLUMNA DERECHA) */}
                <div className="simulator-standings-panel">
                    {/* Tarjeta de campeón mundial matemático */}
                    {champCheck && (
                        <div className="mathematical-champion-card" style={{ borderLeft: `4px solid ${champCheck.color}` }}>
                            <div className="champion-card-body">
                                <h4>{t.mathematicalChamp.replace('🏆', '').trim()}</h4>
                                <div className="champion-driver-title">
                                    <span className="champ-flag">{champCheck.flag}</span>
                                    <h2>{champCheck.driver}</h2>
                                </div>
                                <p className="champ-team-subtitle">{champCheck.team}</p>
                                <p className="champ-details">
                                    Ventaja de <strong>{champCheck.gap} pts</strong> con únicamente <strong>{champCheck.maxPointsRemaining} pts</strong> restantes por disputar.
                                </p>
                            </div>
                        </div>
                    )}

                    {/* Selector de pestañas */}
                    <div className="tab-selector-bar">
                        <button 
                            className={`tab-btn ${standingsTab === 'drivers' ? 'active' : ''}`}
                            onClick={() => setStandingsTab('drivers')}
                        >
                            {t.drivers}
                        </button>
                        <button 
                            className={`tab-btn ${standingsTab === 'constructors' ? 'active' : ''}`}
                            onClick={() => setStandingsTab('constructors')}
                        >
                            {t.constructors}
                        </button>
                    </div>

                    <div className="table-responsive">
                        {standingsTab === 'drivers' ? (
                            <table className="standings-table">
                                <thead>
                                    <tr>
                                        <th style={{ width: '60px' }}>{t.position}</th>
                                        <th>{t.driver}</th>
                                        <th>{t.team}</th>
                                        <th className="text-right">{t.points}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {currentStandings.drivers.map((driver, index) => {
                                        const isMathChamp = champCheck && champCheck.driver === driver.name;

                                        return (
                                            <tr key={driver.name} className={`table-row-hover ${isMathChamp ? 'champion-row' : ''}`}>
                                                <td>{getPositionBadge(index + 1)}</td>
                                                <td>
                                                    <div className="driver-cell">
                                                        <span className="flag">{driver.flag}</span>
                                                        <span className="driver-name-text">
                                                            {driver.name}
                                                        </span>
                                                    </div>
                                                </td>
                                                <td>
                                                    <div className="team-cell">
                                                        <span className="team-indicator" style={{ backgroundColor: driver.color }}></span>
                                                        <span className="team-name-text">{driver.team}</span>
                                                    </div>
                                                </td>
                                                <td className="points-cell text-right bold-points">{driver.points}</td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        ) : (
                            <table className="standings-table">
                                <thead>
                                    <tr>
                                        <th style={{ width: '60px' }}>{t.position}</th>
                                        <th>{t.team}</th>
                                        <th className="text-right">{t.points}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {currentStandings.constructors.map((constructor, index) => (
                                        <tr key={constructor.team} className="table-row-hover">
                                            <td>{getPositionBadge(index + 1)}</td>
                                            <td>
                                                <div className="team-cell">
                                                    <span className="team-indicator thick" style={{ backgroundColor: constructor.color }}></span>
                                                    <span className="team-name-text bold">{constructor.team}</span>
                                                </div>
                                            </td>
                                            <td className="points-cell text-right bold-points">{constructor.points}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Simulator;
